import { Link } from 'react-router-dom';

import {
  Page,
  Button,
  EditOutlinedIcon,
  ContainerColumnLeft,
  LinearProgress,
} from '@occc-app/design-system';
import { Feature, useIsUserPermittedTo } from '@occc/utils-access-control';

import { ScanDataSubmissionGroupDetailsLocationsTableContainer } from './scan-data-submission-group-details-locations-table.container';
import { SFTPAttributesCard } from './sftp-attributes-card.component';
import { useScanDataSubmissionGroupsDetails } from './scan-data-submission-groups-details.container';

import { SubmissionGroupAddLocationsDialog } from '../scan-data-submission-groups-create/components/submission-group-add-locations.dialog';
import { SubmissionGroupRemoveLocationsDialog } from '../scan-data-submission-groups-create/components/submission-group-remove-locations.dialog';

export const ScanDataSubmissionGroupsDetailsPage = () => {
  const {
    id,
    title,
    subtitle,
    sftpCredential,
    search,
    handleOnSearchSubmit,
    paginationProps,
    sftLoadedData,
    locationsLoadedData,
    routeCreator,
    isAddLocationsDialogOpen,
    isRemoveLocationsDialogOpen,
    setIsAddLocationsDialogOpen,
    setIsRemoveLocationsDialogOpen,
    handleAddLocationsToGroup,
    isRemoveMode,
    setIsRemoveMode,
    handleRemoveLocationsFromGroup,
    bulkSelectedItems,
    handleBulkSelectMultiple,
    handleBulkDeselectAll,
    handleItemSelect,
    isUpdatingLocations,
  } = useScanDataSubmissionGroupsDetails();

  const isEditDetailsAllowed = useIsUserPermittedTo(Feature.SCAN_DATA_SUBMISSION_GROUPS_EDIT);
  const canAddRemoveLocations = useIsUserPermittedTo(
    Feature.SCAN_DATA_SUBMISSION_GROUPS_ADD_REMOVE_LOCATIONS,
  );

  const isLoadingLocations =
    locationsLoadedData.loading || (!sftLoadedData.data && sftLoadedData.loading);

  if (sftLoadedData.error) {
    return (
      <Page
        browserTabTitle='Error'
        pageHeaderProps={{ title: 'Error Loading Submission Group' }}
        containerProps={{ variant: 'md' }}
      >
        {sftLoadedData.error.message}
      </Page>
    );
  }

  return (
    <Page
      browserTabTitle={title}
      pageHeaderProps={{
        title,
        subtitle,
      }}
      containerProps={{ variant: 'md' }}
      actions={
        isEditDetailsAllowed && (
          <Button
            color='secondary'
            variant='contained'
            component={Link}
            to={routeCreator.submissionGroupsUpdate(id)}
            startIcon={<EditOutlinedIcon />}
            role='button'
          >
            Edit details
          </Button>
        )
      }
    >
      {sftLoadedData.loading && <LinearProgress />}

      {isAddLocationsDialogOpen && sftpCredential && (
        <SubmissionGroupAddLocationsDialog
          cpg={sftpCredential.cpg}
          open={isAddLocationsDialogOpen}
          setDialogOpen={setIsAddLocationsDialogOpen}
          onSubmit={handleAddLocationsToGroup}
          isUpdatingLocations={isUpdatingLocations}
        />
      )}

      {isRemoveLocationsDialogOpen && (
        <SubmissionGroupRemoveLocationsDialog
          open={isRemoveLocationsDialogOpen}
          setOpen={setIsRemoveLocationsDialogOpen}
          onConfirm={() => handleRemoveLocationsFromGroup(bulkSelectedItems)}
          isRemoveLocationFromSubmissionGroupInProgress={isUpdatingLocations}
          selectedCount={bulkSelectedItems.size}
        />
      )}

      <ContainerColumnLeft>
        <SFTPAttributesCard
          username={sftpCredential?.username || 'N/A'}
          password={sftpCredential?.password || 'N/A'}
          portNumber={sftpCredential?.port?.toString() || 'N/A'}
          submissionUrl={sftpCredential?.host || 'N/A'}
          submissionStatus={sftpCredential?.submissionStatus || 'N/A'}
          submitterId={sftpCredential?.submitterID || 'N/A'}
        />

        <ScanDataSubmissionGroupDetailsLocationsTableContainer
          cpg={sftpCredential?.cpg}
          data={locationsLoadedData.data}
          loading={isLoadingLocations}
          error={locationsLoadedData.error}
          onSearchSubmit={handleOnSearchSubmit}
          search={search}
          onAdd={() => setIsAddLocationsDialogOpen(true)}
          onRemove={() => setIsRemoveMode(true)}
          onRemoveSelected={() => setIsRemoveLocationsDialogOpen(true)}
          paginationProps={paginationProps}
          canAddRemoveLocations={canAddRemoveLocations}
          isRemoveMode={isRemoveMode}
          onRemoveModeChange={setIsRemoveMode}
          bulkSelectedItems={bulkSelectedItems}
          handleBulkSelectMultiple={handleBulkSelectMultiple}
          handleBulkDeselectAll={handleBulkDeselectAll}
          handleItemSelect={handleItemSelect}
        />
      </ContainerColumnLeft>
    </Page>
  );
};
